import { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import StatValue from './StatValue';

// App-local grid for "Double" MatchStats tiles (MatchStats_x2_*). Each tile
// carries an overall graphic title (MatchStats.strTitle) plus two stat rows
// (Stat1/Stat2), each with a home + away value. Styled to match the shared
// @ae/scoreboard CopilotStatsGrid (dark cards, team accent border, grey number
// tiles, yellow focus ring) so Single/Double tabs read as one family. Kept in
// the app to avoid a shared-library rebuild for the MVP.

const CARD_BG = '#2b2b2b';
const TEXT = '#ffffff';
const TEXT_SHADOW = '0 1px 4px rgba(0,0,0,0.7)';
const FALLBACK_HOME = '#2f6fd0';
const FALLBACK_AWAY = '#3aa564';
const LIVE = '#feec67';
// 5 columns like the single grid so tiles keep the same width, but only 4 per
// row: the right-hand column stays empty (OPUS-1842).
const COLS = 5;
const PER_ROW = 4;

const StatRow = ({ title, home, away, scale = 1 }) => {
  const s = (px) => Math.round(px * scale);
  return (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography
      sx={{
        textAlign: 'center',
        fontSize: s(13),
        fontWeight: 700,
        letterSpacing: 0.2,
        textTransform: 'uppercase',
        lineHeight: 1.1,
        color: 'rgba(255,255,255,0.85)',
        textShadow: TEXT_SHADOW,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {title}
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {[home, away].map((val, i) => (
        <Box key={i} sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
          <StatValue value={val} maxPx={s(30)} width="88%" height={s(32)} />
        </Box>
      ))}
    </Box>
  </Box>
  );
};

StatRow.propTypes = {
  title: PropTypes.string,
  home: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  away: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scale: PropTypes.number,
};

export default function CopilotDoubleStatsGrid({ stats = [], leftColor, rightColor, activeKey, onStatClick, scale = 1 }) {
  const home = leftColor || FALLBACK_HOME;
  const away = rightColor || FALLBACK_AWAY;
  // Scale tile height + type with the viewport; 1 at the 1080 baseline.
  const s = (px) => Math.round(px * scale);
  const gridRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const tabIdx = Math.min(activeIdx, Math.max(0, stats.length - 1));

  const handleKeyDown = (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
    const btns = Array.from(gridRef.current?.querySelectorAll('[data-stat-btn]') ?? []);
    if (!btns.length) return;
    const idx = btns.indexOf(document.activeElement);
    if (idx === -1) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? PER_ROW : -PER_ROW;
    const next = Math.max(0, Math.min(btns.length - 1, idx + delta));
    setActiveIdx(next);
    btns[next]?.focus();
  };

  return (
    <Box
      ref={gridRef}
      onKeyDown={handleKeyDown}
      sx={{
        // 8px past the panel on the sides and bottom: room for the tiles'
        // rings, while the tiles themselves line up with the column edges.
        height: 'calc(100% + 8px)',
        width: 'calc(100% + 16px)',
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
        // Three rows share the height the panel gives, so they fit without scrolling.
        gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
        gridAutoRows: `${s(156)}px`,
        gap: 1.5,
        px: 1,
        mx: -1,
        mb: -1,
        pt: 0.75,
        pb: 1,
        alignContent: 'start',
      }}
    >
      {stats.map((stat, i) => {
        const isLive = activeKey && stat.key === activeKey;
        return (
        <Box
          key={stat.key}
          component="button"
          data-stat-btn="true"
          tabIndex={i === tabIdx ? 0 : -1}
          onFocus={() => setActiveIdx(i)}
          onClick={() => onStatClick?.(stat)}
          className="no-drag"
          sx={{
            gridColumn: (i % PER_ROW) + 1,
            position: 'relative',
            display: 'flex',
            p: 0,
            bgcolor: 'transparent',
            border: 'none',
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'box-shadow 0.15s ease, transform 0.1s ease',
            outline: 'none',
            boxShadow: isLive ? `0 0 0 3px ${LIVE}, 0 0 14px rgba(254,236,103,0.5)` : 'none',
            '&:hover': { boxShadow: isLive ? `0 0 0 3px ${LIVE}, 0 0 14px rgba(254,236,103,0.5)` : `0 0 0 2px ${LIVE}` },
            '&:focus-visible': { boxShadow: `0 0 0 2px ${LIVE}` },
            '&:active': { transform: 'scale(0.99)' },
          }}
        >
          {/* Two team-coloured halves with a top accent border. */}
          <Box sx={{ flex: 1, bgcolor: CARD_BG, borderTop: `4px solid ${home}`, borderRadius: '8px 0 0 8px' }} />
          <Box sx={{ flex: 1, bgcolor: CARD_BG, borderTop: `4px solid ${away}`, borderRadius: '0 8px 8px 0' }} />

          {isLive && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, width: 11, height: 11, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px #22c55e', zIndex: 2 }} />
          )}

          {/* Overlay: graphic title on top, then the two stat rows. */}
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', gap: 0.75, px: 1, pt: 0.75, pb: 0.75 }}>
            <Typography
              sx={{
                flexShrink: 0,
                textAlign: 'center',
                fontSize: s(15),
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: TEXT,
                textShadow: TEXT_SHADOW,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {stat.title}
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-around', gap: 0.75 }}>
              <StatRow title={stat.stat1?.title} home={stat.stat1?.home} away={stat.stat1?.away} scale={scale} />
              <StatRow title={stat.stat2?.title} home={stat.stat2?.home} away={stat.stat2?.away} scale={scale} />
            </Box>
          </Box>
        </Box>
        );
      })}
    </Box>
  );
}

CopilotDoubleStatsGrid.propTypes = {
  stats: PropTypes.array,
  leftColor: PropTypes.string,
  rightColor: PropTypes.string,
  onStatClick: PropTypes.func,
  scale: PropTypes.number,
};
